import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { instructorApi } from '../../api/instructor';
import { useUiStore } from '../../store/ui';
import type { StartingEvaluation, StartingEvalQuestion, StartingEvalSection } from '../../types';
import { Badge, Button, Card, cx, type BadgeTone } from '../../components/charte';
import { PageShell } from '../../components/shell/PageShell';

// Évaluation de départ en page (refonte du 14/10/2026, tablette d'abord) : mêmes rubriques, mêmes appels ;
// choix en pilules au doigt, enregistrement collé en bas.

const LEVEL_META: Record<string, { label: string; tone: BadgeTone }> = {
  bon: { label: 'Bon', tone: 'ok' },
  satisfaisant: { label: 'Satisfaisant', tone: 'warn' },
  faible: { label: 'Faible', tone: 'danger' },
};

/** Réponse de l'élève mise en forme (contexte lecture seule). */
function studentAnswerLabel(q: StartingEvalQuestion, raw: string | string[] | undefined): string {
  if (raw == null || (Array.isArray(raw) && raw.length === 0)) return '—';
  const labelOf = (v: string) => q.options?.find((o) => o.value === v)?.label ?? v;
  if (q.type === 'order' && Array.isArray(raw)) return raw.map((v, i) => `${i + 1}. ${labelOf(v)}`).join(' · ');
  if (Array.isArray(raw)) return raw.map(labelOf).join(', ');
  if (q.type === 'text') return raw;
  if (q.type === 'date') return raw.replace(/^(\d{4})-(\d{2})-(\d{2})$/, '$3/$2/$1');
  return labelOf(raw);
}

function InstructorQuestion({ q, value, onChange }: { q: StartingEvalQuestion; value: string | undefined; onChange: (v: string) => void }) {
  return (
    <div className="border-t border-slate-100 py-3.5 first:border-t-0 first:pt-0">
      <p className="mb-2 text-sm font-medium text-heading">{q.label}</p>
      <div className="flex flex-wrap gap-2" role="group" aria-label={q.label}>
        {q.options?.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              aria-pressed={active}
              onClick={() => onChange(opt.value)}
              className={cx(
                'min-h-[40px] rounded-pill border px-4 text-[13px] font-semibold transition-colors',
                active ? 'border-primary bg-primary text-white' : 'border-line bg-white text-secondary hover:border-primary-200',
              )}
              title={opt.points != null ? `${opt.points > 0 ? '+' : ''}${opt.points} pt` : undefined}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      {q.hint && <p className="mt-1.5 text-xs text-muted">{q.hint}</p>}
    </div>
  );
}

function Body({ studentId, data }: { studentId: string; data: StartingEvaluation }) {
  const queryClient = useQueryClient();
  const [answers, setAnswers] = useState<Record<string, string>>(data.instructorAnswers ?? {});
  // Recharge si la donnée serveur change (autre onglet).
  useEffect(() => {
    setAnswers(data.instructorAnswers ?? {});
  }, [data.instructorAnswers]);

  const mutation = useMutation({
    mutationFn: () => instructorApi.setStudentStartingEvaluation(studentId, answers),
    onSuccess: (fresh) => {
      queryClient.setQueryData(['student', studentId, 'starting-evaluation'], fresh);
    },
  });

  const setAnswer = (code: string, v: string) => setAnswers((prev) => ({ ...prev, [code]: v }));
  const total = data.instructorSections.reduce((n, s) => n + s.questions.length, 0);
  const filled = data.instructorSections.reduce((n, s) => n + s.questions.filter((q) => answers[q.code]).length, 0);

  if (data.status !== 'completed') {
    return (
      <Card className="bg-warn-soft p-6 text-center text-sm text-warn-fg">
        L'élève n'a pas encore réalisé sa partie de l'évaluation de départ. Vous pourrez renseigner les rubriques
        pratiques une fois qu'il l'aura complétée.
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {data.final && (
        <Card className="bg-surface-indigo p-5 ring-1 ring-primary-100 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-eyebrow uppercase text-secondary">Résultat final</p>
              <p className="text-[28px] font-semibold leading-tight tracking-[-1px] text-primary-700">
                {data.final.score > 0 ? '+' : ''}
                {data.final.score} pts
              </p>
            </div>
            <Badge tone={LEVEL_META[data.final.level]?.tone ?? 'mute'} size="lg">
              {LEVEL_META[data.final.level]?.label ?? data.final.level}
            </Badge>
          </div>
          <p className="mt-3 border-t border-primary-100 pt-3 text-sm text-body">
            <span className="font-semibold">Formation conseillée :</span> {data.final.proposedTotalHours} h au total, dont{' '}
            {data.final.proposedPracticeHours} h de conduite pratique.
          </p>
          <p className="mt-1 text-[11px] text-muted">
            Barème C.F.A — quiz {data.final.knowledgePoints > 0 ? '+' : ''}
            {data.final.knowledgePoints} pts, pratique {data.final.practicalPoints > 0 ? '+' : ''}
            {data.final.practicalPoints} pts.
          </p>
        </Card>
      )}

      {data.knowledgeMax != null && (
        <Card className="flex flex-wrap gap-x-6 gap-y-2 p-4 text-sm text-secondary sm:p-5">
          <span>
            Connaissance du véhicule (en ligne) :{' '}
            <b className="text-heading">
              {data.knowledgeScore}/{data.knowledgeMax}
            </b>
          </span>
          {data.memoryMax != null && (
            <span>
              Exercices de mémoire réussis :{' '}
              <b className="text-heading">
                {data.memoryScore}/{data.memoryMax}
              </b>
            </span>
          )}
        </Card>
      )}

      <div className="grid gap-5 xl:grid-cols-2 xl:items-start">
        {data.instructorSections.map((section: StartingEvalSection) => (
          <Card key={section.code} className="p-5 sm:p-6">
            <h2 className="text-title-sm text-heading">{section.title}</h2>
            {section.description && <p className="mt-0.5 text-[13px] text-secondary">{section.description}</p>}
            <div className="mt-3">
              {section.questions.map((q) => (
                <InstructorQuestion key={q.code} q={q} value={answers[q.code]} onChange={(v) => setAnswer(q.code, v)} />
              ))}
            </div>
          </Card>
        ))}
      </div>

      {/* Contexte : réponses de l'élève */}
      <details className="rounded-card bg-white p-5 shadow-card sm:p-6">
        <summary className="cursor-pointer text-sm font-semibold text-heading">Réponses de l'élève (pour information)</summary>
        <div className="mt-3 space-y-4">
          {data.definition.sections.map((section) => (
            <div key={section.code}>
              <p className="mb-1 text-eyebrow uppercase text-secondary">{section.title}</p>
              <ul className="divide-y divide-slate-100">
                {section.questions.map((q) => (
                  <li key={q.code} className="flex items-baseline justify-between gap-3 py-1.5 text-sm">
                    <span className="min-w-0 flex-1 text-secondary">{q.label}</span>
                    <span className="max-w-[45%] shrink-0 text-right font-medium text-heading">
                      {studentAnswerLabel(q, data.answers[q.code])}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </details>

      <div className="bottom-above-nav sticky z-20 flex flex-wrap items-center gap-3 rounded-card bg-white/95 p-3 shadow-pop backdrop-blur">
        {mutation.isSuccess && <span className="text-sm font-medium text-success-dark">Enregistré ✓</span>}
        {mutation.isError && <span className="text-sm text-danger-dark">Enregistrement impossible.</span>}
        <Button variant="primary" className="ml-auto" loading={mutation.isPending} disabled={filled === 0} onClick={() => mutation.mutate()}>
          Enregistrer l'évaluation ({filled}/{total})
        </Button>
      </div>
    </div>
  );
}

export default function EvaluationPage() {
  const studentId = useUiStore((s) => s.evalStudentId)!;
  const close = useUiStore((s) => s.closeEvaluation);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['student', studentId, 'starting-evaluation'],
    queryFn: () => instructorApi.getStudentStartingEvaluation(studentId),
  });
  const student = useQuery({ queryKey: ['student', studentId], queryFn: () => instructorApi.getStudent(studentId) });

  return (
    <PageShell
      eyebrow={student.data ? `${student.data.firstName} ${student.data.lastName}` : undefined}
      title="Évaluation de départ"
      onBack={close}
    >
      {isLoading && <p className="text-secondary">Chargement…</p>}
      {isError && (
        <p role="alert" className="rounded-row bg-danger-light px-4 py-3 text-sm text-danger-dark">
          Impossible de charger l'évaluation.
        </p>
      )}
      {data && data.status === 'not_requested' ? (
        <Card className="p-6 text-center text-sm text-secondary">Aucune évaluation de départ n'a été demandée pour cet élève.</Card>
      ) : data ? (
        <Body studentId={studentId} data={data} />
      ) : null}
    </PageShell>
  );
}
